import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Search, Download, Mail, Users, Globe2, TrendingUp,
  Filter, X, Copy, Check, MessageSquare, Briefcase, ChevronDown, ChevronRight, Phone,
} from "lucide-react";
import { adminFetchInquiries, isAdminAuthed } from "@/lib/blogApi";

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};
const fmtDay = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }); }
  catch { return iso; }
};

const csvEscape = (v) => {
  const s = v == null ? "" : String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};
const buildCsv = (rows) => {
  const headers = ["created_at", "company_name", "contact_name", "email", "phone", "country",
                   "buyer_type", "products", "quantity_per_month", "certifications_required",
                   "request_type", "message"];
  const lines = [headers.join(",")];
  rows.forEach((r) => {
    lines.push(headers.map((h) => csvEscape(Array.isArray(r[h]) ? r[h].join("; ") : r[h])).join(","));
  });
  return lines.join("\n");
};

const AdminInquiries = () => {
  const authed = isAdminAuthed();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [country, setCountry] = useState("All");
  const [buyerType, setBuyerType] = useState("All");
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [copiedId, setCopiedId] = useState(null);
  const [expanded, setExpanded] = useState(new Set());

  useEffect(() => {
    if (!authed) return;
    let alive = true;
    setLoading(true);
    adminFetchInquiries()
      .then((r) => { if (alive) setRows(r || []); })
      .catch(() => toast.error("Could not load inquiries"))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [authed]);

  const countries = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => { if (r.country) set.add(r.country); });
    return ["All", ...Array.from(set).sort()];
  }, [rows]);
  const buyerTypes = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => { if (r.buyer_type) set.add(r.buyer_type); });
    return ["All", ...Array.from(set).sort()];
  }, [rows]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const list = rows.filter((r) => {
      if (country !== "All" && r.country !== country) return false;
      if (buyerType !== "All" && r.buyer_type !== buyerType) return false;
      if (!term) return true;
      return (
        (r.company_name || "").toLowerCase().includes(term) ||
        (r.contact_name || "").toLowerCase().includes(term) ||
        (r.email || "").toLowerCase().includes(term) ||
        (r.message || "").toLowerCase().includes(term) ||
        (r.products || []).join(",").toLowerCase().includes(term)
      );
    });
    list.sort((a, b) => {
      const va = (a[sortKey] ?? "").toString();
      const vb = (b[sortKey] ?? "").toString();
      return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
    });
    return list;
  }, [rows, search, country, buyerType, sortKey, sortDir]);

  const stats = useMemo(() => {
    const total = rows.length;
    const now = Date.now();
    const last30 = rows.filter((r) => {
      try { return now - new Date(r.created_at).getTime() < 30 * 86400000; } catch { return false; }
    }).length;
    const uniqueCountries = new Set(rows.map((r) => r.country).filter(Boolean)).size;
    const buyerCounts = {};
    rows.forEach((r) => { if (r.buyer_type) buyerCounts[r.buyer_type] = (buyerCounts[r.buyer_type] || 0) + 1; });
    let top = ["—", 0];
    Object.entries(buyerCounts).forEach(([k, v]) => { if (v > top[1]) top = [k, v]; });
    return { total, last30, uniqueCountries, topBuyer: top[0], topBuyerCount: top[1] };
  }, [rows]);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir("desc"); }
  };
  const toggleExpand = (id) => setExpanded((cur) => {
    const next = new Set(cur);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const exportCsv = () => {
    const csv = buildCsv(filtered);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `spiruva-inquiries-${stamp}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 500);
    toast.success(`Exported ${filtered.length} inquir${filtered.length === 1 ? "y" : "ies"}`);
  };
  const copyEmail = async (id, email) => {
    try {
      await navigator.clipboard.writeText(email);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1600);
    } catch { toast.error("Could not copy email"); }
  };
  const mailtoHref = (r) => {
    const subject = `Re: Your inquiry — ${r.company_name}`;
    const body = `Hi ${r.contact_name || "there"},\n\nThank you for reaching out to Spiruva. I've reviewed your inquiry for ${(r.products || []).join(", ") || "our products"} and would be glad to share commercial details + ship a sample kit.\n\nLet me know a 20-minute window that works for you this week.\n\nBest,\nSpiruva Export Desk\nexport@spiruva.com  ·  spiruva.com`;
    return `mailto:${encodeURIComponent(r.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };
  const clearFilters = () => { setSearch(""); setCountry("All"); setBuyerType("All"); };
  const hasFilter = search || country !== "All" || buyerType !== "All";

  if (!authed) return <Navigate to="/admin" replace />;

  return (
    <div className="pt-[72px] bg-brand-cream/40 min-h-screen" data-testid="page-admin-inquiries">
      <div className="container-wide py-10">
        <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-sm text-brand-black/65 hover:text-brand-teal mb-6">
          <ArrowLeft size={14} /> Back to Dashboard
        </Link>

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
          <div>
            <p className="eyebrow mb-2">◦  B2B Inquiries</p>
            <h1 className="font-display text-4xl text-brand-black">Inbound Inquiries</h1>
            <p className="text-sm text-brand-black/60 mt-2 max-w-xl">
              Sample requests, RFQs, and distributor applications submitted via the public `/inquiry` form. A copy of every inquiry is also emailed in real time.
            </p>
          </div>
          <button
            onClick={exportCsv}
            disabled={filtered.length === 0}
            data-testid="inquiries-export-csv"
            className="btn-primary disabled:opacity-50"
          >
            <Download size={14} /> Export CSV ({filtered.length})
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-brand-green/15 border border-brand-green/15 mb-8">
          {[
            { icon: MessageSquare, l: "Total Inquiries", v: stats.total },
            { icon: TrendingUp, l: "Last 30 Days", v: stats.last30 },
            { icon: Globe2, l: "Unique Countries", v: stats.uniqueCountries },
            { icon: Briefcase, l: "Top Buyer Type", v: stats.topBuyer, sub: stats.topBuyerCount > 0 ? `${stats.topBuyerCount} inquiries` : "" },
          ].map((s) => (
            <div key={s.l} className="bg-white p-6">
              <s.icon size={14} className="text-brand-teal" />
              <p className="font-display text-3xl text-brand-black mt-3 line-clamp-1">{s.v}</p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-brand-black/50 mt-1">{s.l}</p>
              {s.sub && <p className="text-[11px] text-brand-black/55 mt-2">{s.sub}</p>}
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white border border-brand-green/15 p-4 mb-4 flex flex-col lg:flex-row gap-3 lg:items-center">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Search size={14} className="text-brand-black/40 shrink-0" />
            <input
              type="text"
              placeholder="Search by company, contact, email, product, message…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="inquiries-search"
              className="flex-1 bg-transparent text-sm focus:outline-none min-w-0"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Filter size={13} className="text-brand-black/40" />
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              data-testid="inquiries-filter-country"
              className="px-3 py-1.5 bg-brand-cream/40 border border-brand-green/20 text-xs font-mono uppercase tracking-widest focus:outline-none"
            >
              {countries.map((c) => <option key={c}>{c}</option>)}
            </select>
            <select
              value={buyerType}
              onChange={(e) => setBuyerType(e.target.value)}
              data-testid="inquiries-filter-buyer-type"
              className="px-3 py-1.5 bg-brand-cream/40 border border-brand-green/20 text-xs font-mono uppercase tracking-widest focus:outline-none"
            >
              {buyerTypes.map((c) => <option key={c}>{c}</option>)}
            </select>
            {hasFilter && (
              <button
                onClick={clearFilters}
                data-testid="inquiries-clear-filters"
                className="text-xs font-mono uppercase tracking-widest text-brand-black/60 hover:text-brand-gold inline-flex items-center gap-1"
              >
                <X size={12} /> Clear
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-brand-green/15 overflow-x-auto">
          <table className="clinical-table">
            <thead>
              <tr>
                <th className="w-6"></th>
                <th onClick={() => toggleSort("created_at")} className="cursor-pointer">Date</th>
                <th onClick={() => toggleSort("company_name")} className="cursor-pointer">Company</th>
                <th onClick={() => toggleSort("contact_name")} className="cursor-pointer">Contact</th>
                <th>Country</th>
                <th>Buyer Type</th>
                <th>Products</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center text-brand-black/50" data-testid="inquiries-loading">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} className="text-center text-brand-black/50 py-10" data-testid="inquiries-empty">
                  {rows.length === 0
                    ? "No inquiries yet. Submissions from /inquiry will appear here in real time."
                    : "No inquiries match your filters."}
                </td></tr>
              ) : filtered.flatMap((r) => {
                const open = expanded.has(r.id);
                const tr = (
                  <tr key={r.id} data-testid={`inquiry-row-${r.id}`}>
                    <td>
                      <button
                        onClick={() => toggleExpand(r.id)}
                        aria-label={open ? "Collapse details" : "Expand details"}
                        data-testid={`inquiry-expand-${r.id}`}
                        className="text-brand-black/40 hover:text-brand-teal transition"
                      >
                        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </button>
                    </td>
                    <td>
                      <p className="text-xs text-brand-black/80">{fmtDay(r.created_at)}</p>
                      <p className="font-mono text-[10px] uppercase tracking-widest text-brand-black/40 mt-0.5">{fmtDate(r.created_at).split(", ").pop()}</p>
                    </td>
                    <td className="max-w-[200px]">
                      <p className="font-medium text-brand-black text-sm line-clamp-2">{r.company_name}</p>
                    </td>
                    <td>
                      <p className="font-medium text-brand-black text-sm">{r.contact_name || "—"}</p>
                      <button
                        onClick={() => copyEmail(r.id, r.email)}
                        className="inline-flex items-center gap-1.5 text-xs text-brand-teal hover:text-brand-green font-mono mt-0.5"
                        title="Copy email"
                      >
                        {r.email}
                        {copiedId === r.id ? <Check size={11} className="text-brand-green" /> : <Copy size={10} className="opacity-50" />}
                      </button>
                    </td>
                    <td>
                      <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 border border-brand-green/25 text-brand-green bg-brand-green/5">
                        {r.country}
                      </span>
                    </td>
                    <td>
                      <span className="font-mono text-[10px] uppercase tracking-widest text-brand-black/70">
                        {r.buyer_type || "—"}
                      </span>
                    </td>
                    <td className="max-w-[180px]">
                      <div className="flex flex-wrap gap-1">
                        {(r.products || []).slice(0, 3).map((p) => (
                          <span key={p} className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 bg-brand-teal/10 text-brand-teal border border-brand-teal/25">{p}</span>
                        ))}
                        {(r.products || []).length > 3 && (
                          <span className="text-[10px] font-mono text-brand-black/50">+{r.products.length - 3}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <a
                        href={mailtoHref(r)}
                        data-testid={`inquiry-email-${r.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand-green text-brand-cream text-[10px] font-mono uppercase tracking-widest hover:bg-brand-black transition"
                      >
                        <Mail size={11} /> Reply
                      </a>
                    </td>
                  </tr>
                );
                if (!open) return [tr];
                const detail = (
                  <tr key={`${r.id}-detail`} data-testid={`inquiry-detail-${r.id}`} className="bg-brand-cream/40">
                    <td colSpan={8} className="!py-6 !px-8">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <DetailField label="Quantity / month" value={r.quantity_per_month} />
                        <DetailField label="Request Type" value={r.request_type} />
                        <DetailField label="Phone" value={r.phone} icon={Phone} />
                        <DetailField label="Required Certifications" value={r.certifications_required} className="md:col-span-3" />
                        <DetailField label="Message" value={r.message} className="md:col-span-3" multiline />
                      </div>
                    </td>
                  </tr>
                );
                return [tr, detail];
              })}
            </tbody>
          </table>
        </div>

        <p className="text-[11px] text-brand-black/45 mt-8 font-mono">
          Inquiries are stored in MongoDB collection <span className="text-brand-teal">inquiries</span> and a real-time email copy is sent via Resend.
          Click any contact email to copy; "Reply" opens your mail client with a pre-filled outreach template.
        </p>
      </div>
    </div>
  );
};

const DetailField = ({ label, value, multiline = false, className = "", icon: Icon }) => (
  <div className={className}>
    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-black/45 mb-1.5 inline-flex items-center gap-1.5">
      {Icon && <Icon size={11} />} {label}
    </p>
    <p className={`text-sm text-brand-black/80 ${multiline ? "whitespace-pre-wrap leading-relaxed" : ""}`}>
      {value || <span className="text-brand-black/30">—</span>}
    </p>
  </div>
);

export default AdminInquiries;
